import React, { useEffect, useState, useCallback } from "react";
import { X, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Modal di eliminazione definitiva UTENTE (interno o cliente).
 * - Checkbox conferma richiesta prima di poter cliccare "Elimina definitivamente"
 * - `endpoint` è la path DELETE (es. `/internal/settings/internal-users/{id}` o
 *   `/internal/clients/{cid}/client-users/{cu_id}`)
 */
export default function DeleteUserModal({
    open,
    onClose,
    onDeleted,
    user, // { name, email }
    endpoint, // path relative to API base
    title = "Eliminazione definitiva utente",
}) {
    const [confirmed, setConfirmed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) {
            setConfirmed(false);
            setError(null);
        }
    }, [open]);

    const doDelete = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await api.delete(endpoint);
            onDeleted?.();
        } catch (e) {
            setError(e?.response?.data?.detail || "Eliminazione fallita");
        } finally {
            setLoading(false);
        }
    }, [endpoint, onDeleted]);

    if (!open || !user) return null;

    return (
        <div className="lt-modal-backdrop" onClick={loading ? undefined : onClose} data-testid="delete-user-modal">
            <div className="lt-modal lt-modal--small" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
                <div className="lt-modal__head" style={{ borderBottom: "1px solid #F2E9E5" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: 2,
                            background: "rgba(220, 38, 38, 0.1)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <AlertTriangle size={18} color="#DC2626" />
                        </div>
                        <h2 className="lt-modal__title" style={{ margin: 0 }}>{title}</h2>
                    </div>
                    <button className="lt-icon-btn" onClick={onClose} disabled={loading} data-testid="delete-user-close">
                        <X size={16} />
                    </button>
                </div>

                <div style={{ padding: "18px 4px 4px" }}>
                    <p style={{ margin: "0 0 12px", color: "#181B20", fontSize: 14, lineHeight: 1.5 }}>
                        Eliminerà definitivamente{" "}
                        <strong style={{ color: "#181B20" }}>{user.name}</strong>{" "}
                        <span style={{ color: "#5F6560" }}>({user.email})</span>.
                    </p>

                    <div style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#DC2626",
                        marginBottom: 16,
                        fontWeight: 600,
                    }}>
                        Operazione irreversibile
                    </div>

                    <label
                        style={{
                            display: "flex", alignItems: "flex-start", gap: 10,
                            padding: "10px 12px",
                            background: "#FAFAF7",
                            border: "1px solid #EEEFEA",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: 13,
                            color: "#181B20",
                            lineHeight: 1.4,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                            disabled={loading}
                            data-testid="delete-user-confirm-checkbox"
                            style={{ marginTop: 2, accentColor: "#DC2626" }}
                        />
                        <span>Confermo di voler eliminare definitivamente questo utente.</span>
                    </label>

                    {error && (
                        <div style={{
                            marginTop: 12, padding: "10px 12px", fontSize: 13,
                            background: "rgba(220, 38, 38, 0.08)",
                            border: "1px solid rgba(220, 38, 38, 0.25)",
                            borderRadius: 4, color: "#DC2626",
                        }} data-testid="delete-user-error">
                            {error}
                        </div>
                    )}

                    <div className="lt-form-actions" style={{ marginTop: 20 }}>
                        <div style={{ flex: 1 }} />
                        <button
                            type="button"
                            className="lt-btn-secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Annulla
                        </button>
                        <button
                            type="button"
                            onClick={doDelete}
                            disabled={!confirmed || loading}
                            data-testid="delete-user-confirm-btn"
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                padding: "10px 18px",
                                borderRadius: 2,
                                border: "1px solid #DC2626",
                                background: (confirmed && !loading) ? "#DC2626" : "transparent",
                                color: (confirmed && !loading) ? "#FFFFFF" : "#DC2626",
                                fontFamily: "'Archivo', system-ui, sans-serif",
                                fontWeight: 500,
                                fontSize: 14,
                                cursor: (confirmed && !loading) ? "pointer" : "not-allowed",
                                opacity: (confirmed && !loading) ? 1 : 0.4,
                                transition: "background-color .15s ease, color .15s ease",
                            }}
                        >
                            {loading ? "Eliminazione…" : "Elimina definitivamente"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
